<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-sm-12 bg-white shadow-sm rounded-lg border mb-5">
                <form class="m-3" @submit.prevent="submit">
                    <!-- title -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.title }} <span class="required-star"></span>
                        </label>
                        <div class="col-sm-9">
                            <input
                                type="text"
                                class="form-control"
                                maxlength="50"
                                v-model="title"
                            />
                            <span class="text-danger" v-if="errors && errors.title">{{
                                errors.title[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- sub_title -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.sub_title }}
                        </label>
                        <div class="col-sm-9">
                            <input
                                type="text"
                                class="form-control"
                                maxlength="100"
                                v-model="subTitle"
                            />
                            <span class="text-danger" v-if="errors && errors.sub_title">{{
                                errors.sub_title[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- description -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.description }}
                        </label>
                        <div class="col-sm-9">
                            <textarea
                                class="form-control"
                                v-model="description"
                                rows="10"
                            ></textarea>
                            <span class="text-danger" v-if="errors && errors.description">{{
                                errors.description[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- image -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.image }}
                        </label>
                        <div class="col-sm-9">
                            <label for="imageFile" role="button"><img
                                :src="routeData && routeData.image ? routeData.image_url : '/img/no-image.jpg'"
                                class="icon-size-100" id="preview-image" ref="image"></label>
                            <span class="align-top text-danger" type="button" @click="removeImage()" v-if="image">
                                <i class="fas fa-minus-circle fa-lg"></i>
                            </span>
                            <input type="file" class="form-control-file d-none" id="imageFile" accept=".jpeg, .png"
                                @change="uploadImage" />
                            <p class="text-danger" v-if="errors && errors.image">{{ errors.image[0] }}</p>
                        </div>
                    </div>
                    <!-- location -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.route_related_locations }}
                        </label>
                        <div class="col-sm-9">
                            <select id="select-locations" class="form-control"></select>
                            <span class="text-danger" v-if="errors && errors.location_ids">{{
                                errors.location_ids[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- line_color -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.line_color }}
                        </label>
                        <div class="col-sm-1">
                            <input type="color" class="form-control form-control-color" v-model="lineColor" />
                            <span class="text-danger" v-if="errors && errors.line_color">{{
                                errors.line_color[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- line_width -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.line_width }}
                        </label>
                        <div class="col-sm-1">
                            <input
                                type="number"
                                min="0"
                                step="0.25"
                                class="form-control text-center"
                                v-model="lineWidth"
                            />
                            <span class="text-danger" v-if="errors && errors.line_width">{{
                                errors.line_width[0]
                            }}</span>
                        </div>
                    </div>
                    <!-- Map -->
                    <div class="form-group row" :class="geojsonError ? 'border border-danger' : ''">
                        <label class="col-sm-3 col-form-label">
                            Route <span class="required-star"></span>
                        </label>
                        <div class="col-sm-3">
                            <p class="form-text text-danger" v-if="geojsonError">
                                <i class="fas fa-exclamation-triangle"></i> Please draw a route
                            </p>
                        </div>
                        <div id="map" style="width: 100%; height: 800px" class="mh-75"></div>
                    </div>
                    <!-- description -->
                    <div class="form-group row">
                        <label class="col-sm-3 col-form-label">
                            {{ trans.status }}
                        </label>
                        <div class="col-sm-9 mt-2">
                            <div class="custom-control custom-switch">
                                <input
                                    type="checkbox"
                                    class="custom-control-input"
                                    id="customSwitch1"
                                    true-value="1"
                                    false-value="0"
                                    v-model="status"
                                />
                                <label
                                    class="custom-control-label text-muted"
                                    for="customSwitch1"
                                    >{{ trans.status_val[status] }}</label
                                >
                            </div>
                        </div>
                    </div>
                </form>
                <button
                    class="btn btn-primary w-25 center mb-4 mt-4 float-right submit-btn"
                    :disabled="isDisabled"
                >
                    {{ routeData ? trans.btn.update : trans.btn.create }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import MapBox from '../mixins/MapBox/Map';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';

export default {
    mixins: [MapBox],
    props: {
        formAction: {
            type: String,
            required: true
        },

        trans: {
            type: Object,
            required: true
        },
        routeData: {
            type: Object,
            default: function () {
                return null;
            }
        },
        locationList: {
            type: Array,
            default: function () {
                return [];
            }
        }
    },
    computed: {
        geojsonError() {
            for (const key in this.errors) {
                if (key.startsWith('coordinates')) {
                    return this.errors[key];
                }
            }

            return '';
        },
        geojson() {
            if (!this.routeData) return;

            var features = [];
            this.routeData.coordinates.forEach((el) => {
                features.push({
                    type: 'Feature',
                    properties: {},
                    geometry: {
                        coordinates: el,
                        type: 'LineString'
                    }
                });
            });

            return {
                type: 'FeatureCollection',
                features: features
            };
        },
    },
    data() {
        return {
            draw: null,
            title: this.routeData ? this.routeData.title : '',
            status: this.routeData ? this.routeData.status : 0,
            subTitle: this.routeData ? this.routeData.sub_title : '',
            description: this.routeData ? this.routeData.description : '',
            locationIds: [],
            lineColor: '#BF93E4',
            lineWidth: 5,
            locationMarkers: {},
            errors: {},
            isDisabled: false,
            image: this.routeData ? this.routeData.image : null,
            removeImg: 0,
            coordinate: '',
        };
    },
    methods: {
        initDraw() {
            this.draw = new MapboxDraw({
                displayControlsDefault: false,
                controls: {
                    polygon: true,
                    line_string: true,
                    trash: true
                },
                defaultMode: 'draw_line_string',
                styles: styles
            });
            this.mySmartMap.addControl(this.draw);
            if (this.routeData) {
                this.draw.add(this.geojson);
            }
        },
        prepareFormData() {
            let formData = new FormData();
            formData.append('title', this.title);
            formData.append('status', this.status);
            formData.append('coordinates', JSON.stringify(this.coordinate));
            formData.append('sub_title', this.subTitle);
            formData.append('description', this.description);
            formData.append('line_color', this.lineColor);
            formData.append('line_width', this.lineWidth);
            formData.append('location_ids', JSON.stringify(this.locationIds));
            if (this.image instanceof File) {
                formData.append("image", this.image);
            }
            if (this.routeData) {
                formData.append('remove_image', this.removeImg);
                formData.append("_method", "PUT");
            }
            return formData;
        },
        submit() {
            this.isDisabled = true;
            var self = this;
            var features = this.draw.getAll().features;
            var coordinates = [];
            features.forEach((feature) => {
                if (feature.geometry.coordinates.length > 0) {
                    if (feature.geometry.type == 'Polygon') {
                        coordinates.push(feature.geometry.coordinates[0]);
                    } else if (
                        feature.geometry.type == 'LineString' &&
                        feature.geometry.coordinates.length >= 2
                    ) {
                        coordinates.push(feature.geometry.coordinates);
                    }
                }
            });
            this.coordinate = coordinates;

            axios({
                method: 'POST',
                url: this.formAction,
                data: this.prepareFormData(),
                headers: {
                    "content-type": "multipart/form-data",
                },
            })
                .then((response) => {
                    if (response.data.redirectTo != undefined) {
                        window.location.replace(response.data.redirectTo);
                    }
                })
                .catch((error) => {
                    self.isDisabled = false;
                    if (error.response.status == 422) {
                        this.errors = {};
                        this.errors = error.response.data.errors;
                    }
                });
        },
        temporaryAddMarker(props) {
            const marker = new mapboxgl.Marker()
                .setLngLat([props.lng, props.lat])
                .addTo(this.mySmartMap);
            this.locationMarkers[props.id] = marker;
        },
        clickSubmitBtn() {
            var self = this;
            let submitBtns = document.getElementsByClassName('submit-btn');
            if (submitBtns.length > 0) {
                for (let i = 0; i < submitBtns.length; i++) {
                    const btn = submitBtns[i];
                    btn.addEventListener('click', function () {
                        self.submit();
                        this.disabled = true;
                        let formLanguages = document.querySelectorAll(".tab-content form");
                        formLanguages.forEach(function (form) {
                            var formData = new FormData(form);
                            axios.post(form.action, formData, {
                                headers: {
                                    "Content-Type": "multipart/form-data",
                                },
                            })
                        });
                    });
                }
            }
        },
        uploadImage(event) {
            if (event.target.files.length == 0) return;
            const file = event.target.files;
            if (file) {
                let previewImage = document.getElementById("preview-image");
                previewImage.src = URL.createObjectURL(file[0]);
                this.image = file[0];
                this.removeImg = 0;
            }
        },
        removeImage() {
            this.$refs.image.src = "/img/no-image.jpg";
            this.image = null;
            if(this.routeData) {
                this.removeImg = 1;
            }
        },
    },
    created() {
        if (!this.routeData) return;

        this.title = this.routeData.title;
        this.status = this.routeData.status;
        this.subTitle = this.routeData.sub_title;
        this.description = this.routeData.description;
        this.locationIds = this.routeData.locations.map((x) => x.id);
        this.lineColor = this.routeData.line_color;
        this.lineWidth = this.routeData.line_width;
    },
    mounted() {
        this.initDraw();
        var self = this;
        $('#select-locations').selectize({
            maxItems: null,
            valueField: 'id',
            labelField: 'title',
            searchField: 'title',
            options: this.locationList,
            plugins: ['remove_button'],
            create: false,
            items: this.locationIds,
            onChange: function (value) {
                self.locationIds = value;
            }
        });
        this.clickSubmitBtn();
    },
    watch: {
        locationIds(newVal, oldVal) {
            oldVal.forEach((id) => {
                if (!newVal.includes(id)) {
                    this.locationMarkers[id].remove();
                }
            });
            newVal.forEach((id) => {
                if (!oldVal.includes(id)) {
                    const found = this.locationList.find((el) => el.id == id);
                    if (found) {
                        this.temporaryAddMarker(found);
                    }
                }
            });
        }
    }
};

const styles = [
    // ACTIVE (being drawn)
    // line stroke
    {
        id: 'gl-draw-line',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        },
        paint: {
            'line-color': '#D20C0C',
            'line-dasharray': [0.2, 2],
            'line-width': 2
        }
    },
    // polygon fill
    {
        id: 'gl-draw-polygon-fill',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        paint: {
            'fill-color': '#D20C0C',
            'fill-outline-color': '#D20C0C',
            'fill-opacity': 0
        }
    },
    // polygon mid points
    {
        id: 'gl-draw-polygon-midpoint',
        type: 'circle',
        filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
        paint: {
            'circle-radius': 3,
            'circle-color': '#fbb03b'
        }
    },
    // polygon outline stroke
    // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
    {
        id: 'gl-draw-polygon-stroke-active',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        },
        paint: {
            'line-color': '#D20C0C',
            'line-dasharray': [0.2, 2],
            'line-width': 2
        }
    },
    // vertex point halos
    {
        id: 'gl-draw-polygon-and-line-vertex-halo-active',
        type: 'circle',
        filter: [
            'all',
            ['==', 'meta', 'vertex'],
            ['==', '$type', 'Point'],
            ['!=', 'mode', 'static']
        ],
        paint: {
            'circle-radius': 5,
            'circle-color': '#FFF'
        }
    },
    // vertex points
    {
        id: 'gl-draw-polygon-and-line-vertex-active',
        type: 'circle',
        filter: [
            'all',
            ['==', 'meta', 'vertex'],
            ['==', '$type', 'Point'],
            ['!=', 'mode', 'static']
        ],
        paint: {
            'circle-radius': 3,
            'circle-color': '#D20C0C'
        }
    },

    // INACTIVE (static, already drawn)
    // line stroke
    {
        id: 'gl-draw-line-static',
        type: 'line',
        filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        },
        paint: {
            'line-color': '#000',
            'line-width': 3
        }
    },
    // polygon fill
    {
        id: 'gl-draw-polygon-fill-static',
        type: 'fill',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
        paint: {
            'fill-color': '#000',
            'fill-outline-color': '#000',
            'fill-opacity': 0.1
        }
    },
    // polygon outline
    {
        id: 'gl-draw-polygon-stroke-static',
        type: 'line',
        filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
        layout: {
            'line-cap': 'round',
            'line-join': 'round'
        },
        paint: {
            'line-color': '#000',
            'line-width': 3
        }
    }
];
</script>

<style>
</style>